/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { updateField } from 'store/SquaresStore/WorkOrderManagement/WorkOrderCreationFormState.slice';
import {
  FormLabel,
  MemoizedDatePicker,
  MemoizedNumericTextFieldWithIcon,
  MemoizedTextField,
  MemoizedTimeSelectorWithUnit,
} from './CustomInput';
import { formatNumber } from '../../helpers/formatters';
import type { WorkOrderIssue } from 'types/Squares/WorkOrderManagement/work.order.creation.form.types';
import useAppTheme from 'hooks/useAppTheme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const ActualTimeInput = ({
  data,
  index,
}: {
  data: WorkOrderIssue;
  index: number;
}) => {
  const dispatch = useAppDispatch();

  const timeValue = data.actualTime ?? 0;
  const timeUnit = data.actualTimeUnit as 'Hours' | 'Days';

  const error =
    useAppSelector(
      (state) => state.sqauresWorkOrderCreationFormReducer.errors?.actualTime
    ) ?? '';
  const handleTimeUnitChange = (
    _event: React.MouseEvent<HTMLElement>,
    newUnit: 'Hours' | 'Days' | null
  ) => {
    if (newUnit !== null) {
      dispatch(
        updateField({
          key: `components[${index}].actualTimeUnit`,
          value: newUnit,
        })
      );
    }
  };

  const handleTimeValueChange = (amount: number) => {
    const newValue = Math.max(0, Number(timeValue) + amount);
    dispatch(
      updateField({ key: `components[${index}].actualTime`, value: newValue })
    );
  };

  const handleTimeInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const parsedVal = formatNumber(event.target.value, '');
    if (parsedVal === '') {
      dispatch(
        updateField({ key: `components[${index}].actualTime`, value: '' })
      );
    } else if (typeof parsedVal === 'number') {
      if (!isNaN(parsedVal) && parsedVal >= 0) {
        dispatch(
          updateField({
            key: `components[${index}].actualTime`,
            value: parsedVal,
          })
        );
      }
    }
  };

  return (
    <MemoizedTimeSelectorWithUnit
      name="actualTime"
      required
      value={timeValue}
      unit={timeUnit}
      onValueChange={handleTimeInputChange}
      onUnitChange={handleTimeUnitChange}
      onIncrement={() => {
        handleTimeValueChange(1);
      }}
      onDecrement={() => {
        handleTimeValueChange(-1);
      }}
      error={error}
    />
  );
};

export const WorkOrderCompletionStep: React.FC = () => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const dispatch = useAppDispatch();
  const { values } = useAppSelector(
    (state) => state.sqauresWorkOrderCreationFormReducer
  );
  const components: WorkOrderIssue[] = values.components || [];

  const [userModified, setUserModified] = useState({
    partsCost: false,
    laborCost: false,
    totalCost: false,
  });

  const safeNum = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  const sumField = (field: 'partsCost' | 'laborCost' | 'totalCost') =>
    components.reduce((s, c) => s + safeNum(c[field]), 0);

  useEffect(() => {
    // parts
    if (!userModified.partsCost) {
      const sum = sumField('partsCost');
      if (safeNum(values.partsCost) !== sum) {
        dispatch(updateField({ key: 'partsCost', value: sum }));
      }
    }

    // labor
    if (!userModified.laborCost) {
      const sum = sumField('laborCost');
      if (safeNum(values.laborCost) !== sum) {
        dispatch(updateField({ key: 'laborCost', value: sum }));
      }
    }

    // total
    if (!userModified.totalCost) {
      const sum = sumField('totalCost');
      if (safeNum(values.totalCost) !== sum) {
        dispatch(updateField({ key: 'totalCost', value: sum }));
      }
    }
    // deps: components (cost changes), userModified markers, values to compare, dispatch
  }, [
    // watch only what's necessary to trigger recompute when row costs change
    components
      .map(
        (c) =>
          `${c.id ?? ''}-${safeNum(c.partsCost)}-${safeNum(
            c.laborCost
          )}-${safeNum(c.totalCost)}`
      )
      .join('|'),
    userModified.partsCost,
    userModified.laborCost,
    userModified.totalCost,
    // include values for stable reference comparisons (not the whole object ideally)
    values.partsCost,
    values.laborCost,
    values.totalCost,
    dispatch,
  ]);

  const createOverallCostHandlers = (
    fieldKey: 'partsCost' | 'totalCost' | 'laborCost'
  ) => {
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const parsedVal = formatNumber(event.target.value, '');
        const valueToDispatch = parsedVal === '' ? '' : Number(parsedVal);
        dispatch(updateField({ key: fieldKey, value: valueToDispatch }));
        setUserModified((prev) => ({ ...prev, [fieldKey]: true }));
      },
      onIncrement: () => {
        const current = Number(values[fieldKey]) || 0;
        dispatch(updateField({ key: fieldKey, value: current + 50 }));
        setUserModified((prev) => ({ ...prev, [fieldKey]: true }));
      },
      onDecrement: () => {
        const current = Number(values[fieldKey]) || 0;
        dispatch(
          updateField({
            key: fieldKey,
            value: Math.max(0, current - 50),
          })
        );
        setUserModified((prev) => ({ ...prev, [fieldKey]: true }));
      },
    };
  };

  const partsHandlers = createOverallCostHandlers('partsCost');
  const laborHandlers = createOverallCostHandlers('laborCost');
  const totalHandlers = createOverallCostHandlers('totalCost');

  const createRowCostHandlers = (
    index: number,
    fieldKey: 'partsCost' | 'laborCost' | 'totalCost',
    currentValue: number | string | null | undefined
  ) => {
    const cur = Number(currentValue) || 0;
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        const parsedVal = formatNumber(event.target.value, '');
        const valueToDispatch = parsedVal === '' ? '' : Number(parsedVal);
        dispatch(
          updateField({
            key: `components[${index}].${fieldKey}`,
            value: valueToDispatch,
          })
        );
      },
      onIncrement: () => {
        dispatch(
          updateField({
            key: `components[${index}].${fieldKey}`,
            value: cur + 50,
          })
        );
      },
      onDecrement: () => {
        dispatch(
          updateField({
            key: `components[${index}].${fieldKey}`,
            value: Math.max(0, cur - 50),
          })
        );
      },
    };
  };

  const columns = [
    { key: 'description', label: t('issue_description'), width: 280 },
    { key: 'actualStart', label: t('actual_start_date'), width: 160 },
    { key: 'actualEnd', label: t('actual_end_date'), width: 160 },
    { key: 'partsCost', label: t('parts_cost'), width: 160 },
    { key: 'laborCost', label: t('labor_cost'), width: 160 },
    { key: 'totalCost', label: t('total_cost'), width: 160 },
    { key: 'actualTime', label: t('actual_time'), width: 320 },
    { key: 'action', label: t('action_performed'), width: 220 },
  ];

  const totalMinWidth =
    columns.reduce((s, c) => s + c.width, 0) + (columns.length - 1) * 12;

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="h5"
          component="h1"
          fontWeight="medium"
          fontSize={16}
        >
          {t('work_order_completion')}
        </Typography>
        <Typography variant="caption" fontSize={12}>
          {t('mandatory_fields')}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3 }}
        fontSize={12}
      >
        {t('complete_the_work_order_by_logging_the_actual_maintenance_pe')}
      </Typography>

      {/* Horizontal Scroll Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mb: 2,
          p: 1,
          backgroundColor: (theme) => theme.palette.background.paper,
          borderRadius: 1,
          border: '1px solid #FFFFFF26',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontSize={12}>
          {t('scroll_horizontally_to_view_all_columns')}
        </Typography>
      </Box>

      <Box sx={{ overflow: 'auto', flex: 1 }} id="table-container">
        <Box
          display="flex"
          sx={{
            minWidth: totalMinWidth,
            background: palette.toolBarBackground,
            p: 1.5,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            gap: 2,
            position: 'sticky',
            top: 0,
            zIndex: 3,
            alignItems: 'center',
          }}
        >
          {columns.map((col) => (
            <Typography
              key={col.key}
              fontSize={13}
              fontWeight="bold"
              sx={{
                color: '#fff',
                width: col.width,
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
              }}
            >
              {col.label}
            </Typography>
          ))}
        </Box>

        {/* Rows */}
        {components.map((component: WorkOrderIssue, index: number) => {
          const partsRowHandlers = createRowCostHandlers(
            index,
            'partsCost',
            component.partsCost
          );
          const laborRowHandlers = createRowCostHandlers(
            index,
            'laborCost',
            component.laborCost
          );
          const totalRowHandlers = createRowCostHandlers(
            index,
            'totalCost',
            component.totalCost
          );

          return (
            <Box
              key={component.id ?? index}
              display="flex"
              sx={{
                minWidth: totalMinWidth,
                borderTop: '1px solid rgba(255,255,255,0.04)',
                p: 2,
                alignItems: 'center',
                gap: 2,
              }}
            >
              {/* Description */}
              <Box sx={{ width: columns[0].width }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.45,
                  }}
                >
                  {component.description}
                </Typography>
              </Box>

              {/* Actual Start */}
              <Box sx={{ width: columns[1].width }}>
                <MemoizedDatePicker
                  name={`components[${index}].actualStartDate`}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>

              {/* Actual End */}
              <Box sx={{ width: columns[2].width }}>
                <MemoizedDatePicker
                  name={`components[${index}].actualEndDate`}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Box>

              {/* Parts Cost (row) */}
              <Box sx={{ width: columns[3].width }}>
                <MemoizedNumericTextFieldWithIcon
                  name={`components[${index}].partsCost`}
                  value={component.partsCost ?? ''}
                  {...partsRowHandlers}
                />
              </Box>

              {/* Labor Cost (row) */}
              <Box sx={{ width: columns[4].width }}>
                <MemoizedNumericTextFieldWithIcon
                  name={`components[${index}].laborCost`}
                  value={component.laborCost ?? ''}
                  {...laborRowHandlers}
                />
              </Box>

              {/* Total Cost (row) */}
              <Box sx={{ width: columns[5].width }}>
                <MemoizedNumericTextFieldWithIcon
                  name={`components[${index}].totalCost`}
                  value={component.totalCost ?? ''}
                  {...totalRowHandlers}
                />
              </Box>

              {/* Actual Time */}
              <Box sx={{ width: columns[6].width }}>
                <ActualTimeInput data={component} index={index} />
              </Box>

              {/* Action Performed */}
              <Box sx={{ width: columns[7].width }}>
                <MemoizedTextField
                  name={`components[${index}].maintenanceActionPerformed`}
                  select
                  fullWidth
                  size="small"
                  options={[
                    { value: 'Part Replaced', label: t('part_replaced') },
                    { value: 'Part Repaired', label: t('part_repaired') },
                  ]}
                />
              </Box>
            </Box>
          );
        })}

        {/* Overall Values Section - Now inside the scrollable container */}
        <Box
          display="flex"
          sx={{
            position: 'sticky',
            bottom: 0,
            background: (theme) => theme.palette.background.paper,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            p: 2,
            boxShadow: '0 -6px 20px rgba(2,6,23,0.6)',
            minWidth: totalMinWidth,
            gap: 2,
            alignItems: 'center',
          }}
        >
          {/* Description column - empty but maintains spacing */}
          <Box sx={{ width: columns[0].width }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
              {t('work_order_overall_values')}
            </Typography>
          </Box>

          {/* Actual Start */}
          <Box sx={{ width: columns[1].width }}>
            <FormLabel>{t('overall_wo_start_date')}</FormLabel>
            <MemoizedDatePicker
              name={'actualStartDate'}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>

          {/* Actual End */}
          <Box sx={{ width: columns[2].width }}>
            <FormLabel>{t('overall_wo_end_date')}</FormLabel>
            <MemoizedDatePicker
              name={'actualEndDate'}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Box>

          {/* Parts Cost */}
          <Box sx={{ width: columns[3].width }}>
            <FormLabel>{t('overall_parts_cost')}</FormLabel>
            <MemoizedNumericTextFieldWithIcon
              name="partsCost"
              value={values.partsCost ?? ''}
              {...partsHandlers}
            />
          </Box>

          {/* Labor Cost */}
          <Box sx={{ width: columns[4].width }}>
            <FormLabel>{t('overall_labor_cost')}</FormLabel>
            <MemoizedNumericTextFieldWithIcon
              name="laborCost"
              value={values.laborCost ?? ''}
              {...laborHandlers}
            />
          </Box>

          {/* Total Cost */}
          <Box sx={{ width: columns[5].width }}>
            <FormLabel>{t('overall_total_cost')}</FormLabel>
            <MemoizedNumericTextFieldWithIcon
              name="totalCost"
              value={values.totalCost ?? ''}
              {...totalHandlers}
            />
          </Box>

          {/* Actual Time - empty but maintains spacing */}
          <Box sx={{ width: columns[6].width }}>
            {/* Empty space to maintain alignment */}
          </Box>

          {/* Action Performed - empty but maintains spacing */}
          <Box sx={{ width: columns[7].width }}>
            {/* Empty space to maintain alignment */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkOrderCompletionStep;
